import { useId } from "react";
import { cn } from "@/lib/utils";

interface LogoProps {
	className?: string;
	title?: string;
	markOnly?: boolean;
}

const FONT_STACK = "Nunito, Quicksand, 'Arial Rounded MT Bold', Arial, sans-serif";

export function Logo({ className, title = "Jana", markOnly = false }: LogoProps) {
	const id = useId();
	const gradientId = `logo-gradient-${id}`;

	if (markOnly) {
		return (
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 44 44"
				role="img"
				aria-label={title}
				className={className}
			>
				<defs>
					<linearGradient
						id={gradientId}
						x1="0"
						y1="0"
						x2="44"
						y2="44"
						gradientUnits="userSpaceOnUse"
					>
						<stop offset="0" stopColor="#24102D" />
						<stop offset="0.47" stopColor="#4B174A" />
						<stop offset="0.68" stopColor="#9D164F" />
						<stop offset="1" stopColor="#D91D68" />
					</linearGradient>
				</defs>
				<circle cx="22" cy="22" r="22" fill={`url(#${gradientId})`} />
				<text
					x="22"
					y="24"
					textAnchor="middle"
					dominantBaseline="central"
					fontFamily={FONT_STACK}
					fontSize="25"
					fontWeight="700"
					fill="#fff"
				>
					j
				</text>
			</svg>
		);
	}

	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			id="Layer_2"
			viewBox="0 0 165.29 48.774"
			role="img"
			aria-label={title}
			className={cn("h-8 w-auto", className)}
		>
			<defs>
				<style>{".cls-1{fill:#fefffe}.cls-3{fill:#fffefe}.cls-5{fill:#fefefe}"}</style>
			</defs>
			<g id="Layer_1-2">
				<rect
					width={165.29}
					height={48.774}
					rx={24.387}
					ry={24.387}
					style={{
						fill: "#9e1753",
					}}
				/>
				<rect
					width={96.903}
					height={48.774}
					rx={24.387}
					ry={24.387}
					style={{
						fill: "#39053b",
					}}
				/>
				<g id="Generative_Object">
					<path
						d="M49.089 20.693c-1.744-.041-3.094 1.168-3.106 2.89l-.046 6.634c-.006.848-.826 1.299-1.606 1.263-.783-.037-1.417-.537-1.417-1.374l-.002-10.69c-.009-.898.668-1.418 1.507-1.388.902.033 1.534.633 1.463 1.654.96-1.3 2.36-1.825 3.912-1.818 3.308.016 5.058 2.611 5.057 5.812v6.438c0 .894-.724 1.38-1.563 1.367-.767-.011-1.459-.476-1.467-1.305l-.068-6.804c-.016-1.532-1.113-2.643-2.664-2.68Z"
						className="cls-5"
					/>
					<circle cx={84.769} cy={24.409} r={4.752} className="cls-5" />
					<path
						d="M21.963 36.16c-1.353.856-3.656 1.215-4.34-.075-.288-.544-.278-1.174.077-1.662.262-.36.719-.498 1.16-.463.862.07 1.61-.36 1.924-1.156.2-.506.287-1.023.288-1.606l.005-11.886c0-.909.637-1.421 1.507-1.442.875-.02 1.566.507 1.567 1.438l.004 11.782c0 2.001-.52 4.011-2.192 5.07ZM24.444 13.348a2.035 2.035 0 0 1-.453 1.853 1.863 1.863 0 0 1-1.893.534c-.855-.25-1.373-1.027-1.397-1.848-.025-.861.502-1.688 1.379-1.96 1.044-.323 2.093.318 2.364 1.421Z"
						className="cls-5"
					/>
					<path
						d="M70.348 24.326c-.011-1.614-.635-3.127-1.691-4.33-2.593-2.948-7.735-2.814-10.072.234-2.058 2.693-2.058 6.577.079 9.181 2.225 2.715 6.398 3.038 8.657.212.055.545.045 1 .356 1.357.29.345.724.512 1.313.49.79-.022 1.38-.579 1.38-1.358l-.022-5.786Zm-3.494 2.57c-.69 1.302-1.948 1.926-3.316 1.87-1.347-.056-2.516-.913-3.072-2.226a4.761 4.761 0 0 1-.11-3.438c.5-1.436 1.757-2.36 3.16-2.427 1.502-.055 2.814.713 3.45 2.126a4.614 4.614 0 0 1-.112 4.095Z"
						className="cls-1"
					/>
					<path
						d="M38.777 20.587c-1.246-1.825-3.271-2.727-5.486-2.716-2.448.022-4.63 1.258-5.742 3.45-1.035 2.059-1.113 4.507-.167 6.6.957 2.136 2.927 3.56 5.241 3.671 1.725.078 3.228-.556 4.34-1.947.068.512.056.957.357 1.313.3.356.69.523 1.29.512.813-.022 1.414-.59 1.414-1.38V24.65a7.14 7.14 0 0 0-1.247-4.062Zm-2.17 6.12a3.58 3.58 0 0 1-3.16 2.07c-1.38.045-2.615-.723-3.25-2.014-.656-1.336-.645-2.95.078-4.24.68-1.202 1.859-1.87 3.183-1.848 1.28.034 2.46.724 3.082 1.948a4.581 4.581 0 0 1 .067 4.084ZM149.751 19.764l-3.506 10.712c-.2.61-.768.914-1.357.934-.878.096-1.488-.417-1.741-1.213l-2.166-6.809-2.346 7.257c-.185.574-.859.773-1.357.778-.497.005-1.269-.166-1.468-.77l-3.596-10.914c-.142-.43.044-.911.248-1.198.248-.346.706-.46 1.145-.457.8.005 1.234.4 1.45 1.108l2.262 7.403 2.392-7.65c.17-.544.743-.76 1.188-.766.456-.006 1.125.107 1.294.646l2.452 7.844 2.34-7.656c.194-.63.734-.964 1.4-.928.414-.016.836.113 1.082.409.223.267.446.776.284 1.27Z"
						className="cls-3"
					/>
					<path
						d="M112.548 21.086c-1.095-.65-2.524-.455-3.41.386-.563.533-.85 1.312-.855 2.09l-.038 6.54c-.005.804-.646 1.293-1.398 1.318-.81.028-1.49-.476-1.49-1.335V19.328c0-.81.743-1.296 1.486-1.26.834.041 1.35.587 1.356 1.523 1.003-1.258 2.438-1.796 4.007-1.713 1.544.082 2.863.794 3.662 2.139.586.987.896 2.13.9 3.328l.017 6.77c.003.84-.747 1.334-1.511 1.308-.8-.026-1.379-.527-1.384-1.334l-.04-6.523c-.006-.99-.413-1.951-1.302-2.48ZM130.507 20.898c-1.058-1.803-2.883-2.87-4.964-3.005-2.37-.155-4.54.924-5.775 2.972-1.302 2.159-1.391 4.852-.323 7.144.98 2.125 2.971 3.461 5.375 3.572 2.27.1 4.251-.89 5.497-2.715 1.603-2.37 1.636-5.497.19-7.968Zm-2.315 5.898c-.6 1.291-1.747 2.003-3.038 2.003-1.347 0-2.504-.723-3.094-2.003-.656-1.413-.634-3.104.167-4.451.635-1.057 1.67-1.68 2.827-1.692 1.202-.022 2.292.568 2.949 1.659.79 1.324.868 3.037.19 4.484Z"
						className="cls-1"
					/>
				</g>
			</g>
		</svg>
	);
}
